import React, { createContext, useContext, useReducer, useEffect, useRef ,useState} from "react";

const UserContext = createContext();

const loadState = () => {
  try {
    const serializedState = localStorage.getItem("userState");
    if (serializedState === null) {
      return undefined;
    }
    const parsed = JSON.parse(serializedState);

    // Validate the loaded state structure
    if (typeof parsed === 'object' && parsed !== null) {
      return {
        user: parsed.user || null,
        isAuthenticated: Boolean(parsed.isAuthenticated && parsed.user),
        features: Array.isArray(parsed.features) ? parsed.features : [],
        organization: parsed.organization || null,
      };
    }
    return undefined;
  } catch (err) {
    console.warn("Failed to load user state from localStorage:", err);
    return undefined;
  }
};

const saveState = (state) => {
  try {
    // Only save if we have a complete state object
    if (state && typeof state === 'object') {
      const stateToSave = {
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        features: state.features || [],
        organization:state.organization || null,
      };
      const serializedState = JSON.stringify(stateToSave);
      localStorage.setItem("userState", serializedState);
    }
  } catch (error) {
    console.warn("Failed to save user state to localStorage:", error);
    // If saving fails, try to clear the corrupted data
    try {
      localStorage.removeItem("userState");
    } catch (clearError) {
      console.warn("Failed to clear corrupted localStorage:", clearError);
    }
  }
};

const initialState = loadState() || {
  user: null,
  isAuthenticated: false,
  features: [],
  organization: null,
};

function userReducer(state, action) {
  switch (action.type) {
    case "SET_USER":
      if (!action.payload) {
        console.warn("SET_USER called with empty payload");
        return state;
      }
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true
      };

    case "SET_FEATURES":
      if (!Array.isArray(action.payload)) {
        console.warn("SET_FEATURES called with non-array payload");
        return state;
      }
      return {
        ...state,
        features: action.payload
      };
    case "SET_ORGANIZATION":
      if (!action.payload) {
        console.warn("SET_ORGANIZATION called with empty payload");
        return state; 
      }
      return {  
        ...state,
        organization: action.payload
      };

 case "UPDATE_ORGANIZATION":
      if (!state.organization || !action.payload) {
        return state;
      }
      return {
        ...state,
        organization: { ...state.organization, ...action.payload }
      };


    case "CLEAR_USER":
      // Clear localStorage when clearing user
      try {
        localStorage.removeItem("userState");
        sessionStorage.removeItem("authToken");
        // Clear any cookies
        if (typeof document !== 'undefined') {
          document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        }
      } catch (error) {
        console.warn("Failed to clear stored auth data:", error);
      }
      return {
        user: null,
        features: [],
        isAuthenticated: false,
        organization: null
      };

    case "UPDATE_USER":
      if (!state.user || !action.payload) {
        return state;
      }
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };

    default:
      return state;
  }
}

export function UserProvider({ children }) {
  const [state, dispatch] = useReducer(userReducer, initialState);
  const [loading, setLoading] = useState(true);
  const [isDemo,setIsDemo]=useState(localStorage.getItem("isDemo") === "true");
  const saveTimeoutRef = useRef(null);
  

  const updateDemo = () => {
    setIsDemo(true);
    localStorage.setItem("isDemo", true);
  };

  useEffect(() => {
    // Simulate async loading (in case localStorage is slow)
    setLoading(false); // Set to false after initial load
  }, []);

  // Debounced save to prevent excessive localStorage writes
  useEffect(() => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set a new timeout to save the state
    saveTimeoutRef.current = setTimeout(() => {
      saveState(state);
    }, 500); // Save after 500ms of inactivity

    // Cleanup function
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [state]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Enhanced dispatch with error handling
  const enhancedDispatch = (action) => {
    try {
      dispatch(action);
    } catch (error) {
      console.error("Error dispatching action:", error, action);
    }
  };

  const contextValue = {
    state,
    dispatch: enhancedDispatch,
    loading,
    // Utility functions
    isLoggedIn: () => state.isAuthenticated && state.user,
    hasFeature: (featureName) => state.features.some(f => f.name === featureName),
    getUserRole: () => state.user?.role || null,
    isDemo,
    updateDemo,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}